import React from "react";
import { Editor } from "@tinymce/tinymce-react";

import tinymce from "tinymce";

import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/models/dom";

import "tinymce/skins/ui/oxide/skin.css";
import "tinymce/skins/content/default/content.css";

import "tinymce/plugins/advlist";
import "tinymce/plugins/autolink";
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/table";
import "tinymce/plugins/code";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/preview";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/wordcount";

import "tinymce-i18n/langs6/it.js";

import "bootstrap/dist/css/bootstrap.min.css";

const bootstrapTableStyles = [
  {
    text: "Bootstrap - Base",
    value: "table"
  },
  {
    text: "Bootstrap - Striped",
    value: "table table-striped"
  },
  {
    text: "Bootstrap - Bordered",
    value: "table table-bordered"
  },
  {
    text: "Bootstrap - Borderless",
    value: "table table-borderless"
  },
  {
    text: "Bootstrap - Hover",
    value: "table table-hover"
  },
  {
    text: "Bootstrap - Small",
    value: "table table-sm"
  },
  {
    text: "Bootstrap - Dark",
    value: "table table-dark"
  },
  {
    text: "Bootstrap - Striped + Bordered",
    value: "table table-striped table-bordered"
  },
  {
    text: "Bootstrap - Hover + Bordered",
    value: "table table-hover table-bordered"
  },
  {
    text: "Bootstrap - Dark + Bordered",
    value: "table table-dark table-bordered"
  },
  {
    text: "Bootstrap - Dark + Striped",
    value: "table table-dark table-striped"
  },
  {
    text: "Bootstrap - Dark + Striped + Bordered",
    value: "table table-dark table-striped table-bordered"
  }
];

const bootstrapTableClasses = [
  "table",
  "table-striped",
  "table-bordered",
  "table-borderless",
  "table-hover",
  "table-sm",
  "table-dark"
];

const resetTableCellColors = (
  editor,
  table
) => {
  if (!table) {
    return;
  }

  table
    .querySelectorAll("td, th")
    .forEach((cell) => {
      cell.style.removeProperty(
        "background-color"
      );

      cell.style.removeProperty(
        "--bs-table-bg"
      );

      cell.style.removeProperty(
        "color"
      );

      cell.style.removeProperty(
        "--bs-table-color"
      );
    });
};


const getTable = (editor) => {
  return editor.dom.getParent(
    editor.selection.getStart(),
    "table"
  );
};

const getCell = (editor) => {
  return editor.dom.getParent(
    editor.selection.getStart(),
    "td,th"
  );
};

const isImportedTable = (table) => {
  return (
    !!table &&
    table.getAttribute(
      "data-imported-table"
    ) === "true"
  );
};

const isBootstrapTable = (table) => {
  return (
    !!table &&
    !isImportedTable(table) &&
    table.classList.contains("table")
  );
};

const getTableClassValue = (table) => {
  if (!table || isImportedTable(table)) {
    return "custom";
  }

  const currentClasses = new Set(
    bootstrapTableClasses.filter(
      (className) =>
        table.classList.contains(className)
    )
  );

  const match =
    bootstrapTableStyles.find((style) => {
      const styleClasses = new Set(
        style.value
          .split(" ")
          .filter(Boolean)
      );

      if (
        styleClasses.size !==
        currentClasses.size
      ) {
        return false;
      }

      return [...styleClasses].every(
        (className) =>
          currentClasses.has(className)
      );
    });

  return match ? match.value : "table";
};

const getColorFromStyle = (
  element,
  property
) => {
  if (!element) {
    return "";
  }

  return element.style
    .getPropertyValue(property)
    .trim();
};

const normalizeColor = (color) => {
  if (!color) {
    return "";
  }

  if (color.startsWith("#")) {
    return color;
  }

  const match = color.match(
    /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/
  );

  if (!match) {
    return color;
  }

  return (
    "#" +
    [match[1], match[2], match[3]]
      .map((value) =>
        Number(value)
          .toString(16)
          .padStart(2, "0")
      )
      .join("")
  );
};

const getComputedColor = (
  element,
  property
) => {
  if (!element) {
    return "";
  }

  const value = window
    .getComputedStyle(element)
    .getPropertyValue(property)
    .trim();

  return normalizeColor(value);
};

const getTableBackgroundColor = (
  table
) => {
  if (!table) {
    return "";
  }

  const inline = getColorFromStyle(
    table,
    "--bs-table-bg"
  );

  if (inline) {
    return normalizeColor(inline);
  }

  const background =
    table.style.backgroundColor;

  if (background) {
    return normalizeColor(background);
  }

  if (isImportedTable(table)) {
    return "";
  }

  const firstCell =
    table.querySelector("td") ||
    table.querySelector("th");

  return getComputedColor(
    firstCell || table,
    "background-color"
  );
};

const getTableBorderColor = (
  table
) => {
  if (!table) {
    return "";
  }

  const inline = getColorFromStyle(
    table,
    "--bs-table-border-color"
  );

  if (inline) {
    return normalizeColor(inline);
  }

  if (isImportedTable(table)) {
    return "";
  }

  const firstCell =
    table.querySelector("td") ||
    table.querySelector("th");

  if (!firstCell) {
    return "";
  }

  return getComputedColor(
    firstCell,
    "border-top-color"
  );
};

const getTableWidth = (table) => {
  if (!table) {
    return "";
  }

  const inlineWidth =
    table.style.width ||
    table.getAttribute("width");

  if (inlineWidth) {
    return inlineWidth;
  }

  const computedWidth =
    window
      .getComputedStyle(table)
      .width;

  if (computedWidth) {
    return `${Math.round(
      parseFloat(computedWidth)
    )}px`;
  }

  const rect =
    table.getBoundingClientRect();

  return rect.width
    ? `${Math.round(rect.width)}px`
    : "";
};

const getTableHeight = (table) => {
  if (!table) {
    return "";
  }

  const inlineHeight =
    table.style.height ||
    table.getAttribute("height");

  if (inlineHeight) {
    return inlineHeight;
  }

  const computedHeight =
    window
      .getComputedStyle(table)
      .height;

  if (computedHeight) {
    return `${Math.round(
      parseFloat(computedHeight)
    )}px`;
  }

  const rect =
    table.getBoundingClientRect();

  return rect.height
    ? `${Math.round(rect.height)}px`
    : "";
};

const getElementWidth = (element) => {
  if (!element) {
    return "";
  }

  const inlineWidth =
    element.style.width ||
    element.getAttribute("width");

  if (inlineWidth) {
    return inlineWidth;
  }

  const computedWidth =
    window.getComputedStyle(element).width;

  if (computedWidth) {
    return `${Math.round(
      parseFloat(computedWidth)
    )}px`;
  }

  const rect =
    element.getBoundingClientRect();

  return rect.width
    ? `${Math.round(rect.width)}px`
    : "";
};

const getElementHeight = (element) => {
  if (!element) {
    return "";
  }

  const inlineHeight =
    element.style.height ||
    element.getAttribute("height");

  if (inlineHeight) {
    return inlineHeight;
  }

  const computedHeight =
    window.getComputedStyle(element).height;

  if (computedHeight) {
    return `${Math.round(
      parseFloat(computedHeight)
    )}px`;
  }

  const rect =
    element.getBoundingClientRect();

  return rect.height
    ? `${Math.round(rect.height)}px`
    : "";
};

const normalizeWidthValue = (value) => {
  if (!value) {
    return "";
  }

  if (/^\d+$/.test(value)) {
    return `${value}px`;
  }

  if (/^\d+(\.\d+)?%$/.test(value)) {
    return value;
  }

  if (/^\d+(\.\d+)?px$/i.test(value)) {
    return value;
  }

  return value;
};

const normalizeHeightValue = (value) => {
  if (!value) {
    return "";
  }

  if (/^\d+$/.test(value)) {
    return `${value}px`;
  }

  if (/^\d+(\.\d+)?%$/.test(value)) {
    return value;
  }

  if (/^\d+(\.\d+)?px$/i.test(value)) {
    return value;
  }

  return value;
};

const resetTableColors = (
  editor,
  table
) => {
  editor.dom.setStyle(
    table,
    "--bs-table-bg",
    ""
  );

  editor.dom.setStyle(
    table,
    "--bs-table-border-color",
    ""
  );

  editor.dom.setStyle(
    table,
    "background-color",
    ""
  );

  table.style.removeProperty(
    "border-color"
  );
};

const getCellBackgroundColor = (
  cell
) => {
  if (!cell) {
    return "";
  }

  const background =
    cell.style.backgroundColor;

  if (background) {
    return normalizeColor(background);
  }

  const inline = getColorFromStyle(
    cell,
    "--bs-table-bg"
  );

  if (inline) {
    return normalizeColor(inline);
  }

  return "";
};

const getCellTextColor = (cell) => {
  if (!cell) {
    return "";
  }

  const color = cell.style.color;

  if (color) {
    return normalizeColor(color);
  }

  const inline = getColorFromStyle(
    cell,
    "--bs-table-color"
  );

  if (inline) {
    return normalizeColor(inline);
  }

  return "";
};

const setImportantStyle = (
  element,
  property,
  value
) => {
  if (!element) {
    return;
  }

  if (value) {
    element.style.setProperty(
      property,
      value,
      "important"
    );
  } else {
    element.style.removeProperty(
      property
    );
  }
};

const cleanImportedTable = (html) => {
  if (!html) {
    return null;
  }

  const parser = new DOMParser();

  const doc = parser.parseFromString(
    html,
    "text/html"
  );

  const tables = [
    ...doc.querySelectorAll("table")
  ];

  if (!tables.length) {
    return null;
  }

  const table = tables[0];

  doc
    .querySelectorAll(
      "script, meta, link, xml"
    )
    .forEach((element) => {
      element.remove();
    });

  doc
    .querySelectorAll("*")
    .forEach((element) => {
      [
        ...element.attributes
      ].forEach((attribute) => {
        const name =
          attribute.name.toLowerCase();

        const attributeValue =
          attribute.value || "";

        if (
          name.startsWith("on") ||
          name.startsWith("xmlns") ||
          name.startsWith("data-mso") ||
          name.startsWith("mso-")
        ) {
          element.removeAttribute(
            attribute.name
          );

          return;
        }

        if (
          name === "style" &&
          /mso-[^:;]+:/i.test(
            attributeValue
          )
        ) {
          const cleanedStyle =
            attributeValue
              .split(";")
              .map((rule) =>
                rule.trim()
              )
              .filter(
                (rule) =>
                  rule &&
                  !/^mso-[^:]+:/i.test(
                    rule
                  )
              )
              .join("; ");

          if (cleanedStyle) {
            element.setAttribute(
              "style",
              cleanedStyle
            );
          } else {
            element.removeAttribute(
              "style"
            );
          }
        }
      });
    });

  table.classList.remove(
    "table",
    "table-striped",
    "table-bordered",
    "table-borderless",
    "table-hover",
    "table-sm",
    "table-dark"
  );

  table.setAttribute(
    "data-imported-table",
    "true"
  );

  return table.outerHTML;
};

const importTableFromClipboard = (
  editor,
  clipboardData
) => {
  if (!clipboardData) {
    return false;
  }

  const html =
    clipboardData.getData(
      "text/html"
    );

  if (!html) {
    return false;
  }

  const importedTable =
    cleanImportedTable(html);

  if (!importedTable) {
    return false;
  }

  editor.insertContent(
    importedTable
  );

  return true;
};

const getTableColumnCount = (table) => {
  if (!table || !table.rows.length) {
    return 0;
  }

  let maxColumns = 0;

  Array.from(table.rows).forEach(
    (row) => {
      const count = Array.from(
        row.cells
      ).reduce(
        (total, cell) =>
          total + (cell.colSpan || 1),
        0
      );

      maxColumns = Math.max(
        maxColumns,
        count
      );
    }
  );

  return maxColumns;
};

const normalizeTableColumnWidths = (
  editor,
  table
) => {
  if (!table || isImportedTable(table)) {
    return;
  }

  const columnCount =
    getTableColumnCount(table);

  if (!columnCount) {
    return;
  }

  const width =
    `${100 / columnCount}%`;

  const colgroup =
    table.querySelector("colgroup");

  if (colgroup) {
    const cols = Array.from(
      colgroup.querySelectorAll("col")
    );

    cols.forEach((col) => {
      editor.dom.setStyle(
        col,
        "width",
        width
      );
    });
  }

  Array.from(table.rows).forEach(
    (row) => {
      Array.from(row.cells).forEach(
        (cell) => {
          if (
            (cell.colSpan || 1) === 1
          ) {
            editor.dom.setStyle(
              cell,
              "width",
              width
            );
          }
        }
      );
    }
  );
};

const createTableHead = (
  editor,
  table
) => {
  if (!table || isImportedTable(table)) {
    return;
  }

  if (table.querySelector("thead")) {
    return;
  }

  const tbody =
    table.querySelector("tbody");

  if (!tbody) {
    return;
  }

  const firstRow =
    tbody.querySelector("tr");

  if (!firstRow) {
    return;
  }

  const thead =
    editor.getDoc().createElement(
      "thead"
    );

  thead.appendChild(firstRow);

  table.insertBefore(
    thead,
    tbody
  );

  Array.from(
    thead.querySelectorAll("td")
  ).forEach((cell) => {
    const th =
      editor.getDoc().createElement(
        "th"
      );

    Array.from(
      cell.attributes
    ).forEach((attribute) => {
      th.setAttribute(
        attribute.name,
        attribute.value
      );
    });

    th.innerHTML =
      cell.innerHTML;

    cell.replaceWith(th);
  });

  normalizeTableColumnWidths(
    editor,
    table
  );
};

const getImportedTableAlignment = (
  table
) => {
  if (!table) {
    return "";
  }

  const marginLeft =
    table.style.marginLeft;

  const marginRight =
    table.style.marginRight;

  if (
    marginLeft === "auto" &&
    marginRight === "auto"
  ) {
    return "center";
  }

  if (
    marginLeft === "auto" &&
    marginRight !== "auto"
  ) {
    return "right";
  }

  return "";
};

const TinyMCEEditor = ({
  value,
  onChange,
  placeholder
}) => {
  return (
    <Editor
      tinymce={tinymce}
      value={value || ""}
      onEditorChange={onChange}
      licenseKey="gpl"
      init={{
        height: 500,
        language: "it",
        menubar: false,
        contextmenu: false,
        ui_mode: "split",
        highlight_on_focus: false,
        automatic_uploads: true,
        file_picker_types: "image",

        file_picker_callback: (
          callback,
          value,
          meta
        ) => {
          if (meta.filetype !== "image") {
            return;
          }

          const input =
            document.createElement(
              "input"
            );

          input.type = "file";
          input.accept = "image/*";

          input.onchange = () => {
            const file =
              input.files?.[0];

            if (!file) {
              return;
            }

            const reader =
              new FileReader();

            reader.onload = () => {
              callback(
                reader.result,
                {
                  alt: file.name
                }
              );
            };

            reader.readAsDataURL(
              file
            );
          };

          input.click();
        },

        content_css:
          "/css/bootstrap.min.css",

        content_style: `
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Encode+Sans+Semi+Expanded:wght@100;200;300;400;500;600;700;800;900&family=Montserrat:ital,wght@0,100..900;1,100..900&family=MuseoModerno:ital,wght@0,100..900;1,100..1000&family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&family=Outfit:wght@100..900&family=Questrial&display=swap');

          html {
            background: #f9f9fb;
          }

          body {
            background-color: #f9f9fb !important;
            padding: 24px;
            font-family: 'Outfit', sans-serif !important;
          }

          p {
            margin: 0 !important;
          }

          table.table {
            margin-top: 1rem;
            margin-bottom: 1rem;
          }

          table[data-imported-table="true"] {
            margin-top: 1rem;
            margin-bottom: 1rem;
            box-shadow: none;
          }

          table[data-imported-table="true"] tr {
            box-shadow: none;
          }

          table[data-imported-table="true"] td,
          table[data-imported-table="true"] th {
            background-image: none;
            box-shadow: none;
          }

          ::-webkit-scrollbar {
            width: 8px;
          }

          ::-webkit-scrollbar-track {
            background: #f9f9fb !important;
          }

          ::-webkit-scrollbar-thumb {
            background: #bdbdbd;
            border-radius: 10px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: #999999;
          }
        `,

        table_default_attributes: {
          class: "table table-bordered"
        },

        table_default_styles: {
          width: "100%",
          "border-collapse": "collapse"
        },

        table_use_colgroups: true,

        table_style_by_css: true,

        table_toolbar:
          "customTableProps customTableCellProps tabledelete | " +
          "tableinsertrowbefore tableinsertrowafter tabledeleterow | " +
          "tableinsertcolbefore tableinsertcolafter tabledeletecol",

        plugins: [
          "advlist",
          "autolink",
          "lists",
          "link",
          "image",
          "table",
          "code",
          "fullscreen",
          "preview",
          "searchreplace",
          "visualblocks",
          "wordcount"
        ],

        toolbar:
          "undo redo | " +
          "blocks | " +
          "bold italic underline forecolor backcolor | " +
          "alignleft aligncenter alignright alignjustify | " +
          "bullist numlist | " +
          "customInsertTable | " +
          "link image | " +
          "removeformat | " +
          "code fullscreen",

        setup: (editor) => {
          editor.ui.registry.addMenuButton(
            "customInsertTable",
            {
              icon: "table",
              tooltip:
                "Inserisci tabella",

              fetch: (callback) => {
                callback([
                  {
                    type:
                      "fancymenuitem",

                    fancytype:
                      "inserttable",

                    onAction: (data) => {
                      editor.execCommand(
                        "mceInsertTable",
                        false,
                        {
                          rows:
                            data.numRows,
                          columns:
                            data.numColumns
                        }
                      );

                      setTimeout(() => {
                        const table =
                          getTable(editor);

                        if (!table) {
                          return;
                        }

                        createTableHead(
                          editor,
                          table
                        );

                        editor.nodeChanged();
                      }, 0);
                    }
                  }
                ]);
              }
            }
          );

          editor.on(
            "paste",
            (event) => {
              const clipboardData =
                event.clipboardData;

              if (!clipboardData) {
                return;
              }

              const html =
                clipboardData.getData(
                  "text/html"
                );

              if (!html) {
                return;
              }

              const doc =
                new DOMParser()
                  .parseFromString(
                    html,
                    "text/html"
                  );

              const hasTable =
                !!doc.querySelector(
                  "table"
                );

              if (!hasTable) {
                return;
              }

              const imported =
                importTableFromClipboard(
                  editor,
                  clipboardData
                );

              if (imported) {
                event.preventDefault();
              }
            }
          );

          editor.on(
            "ExecCommand",
            (event) => {
              if (
                event.command ===
                  "mceTableInsertColBefore" ||
                event.command ===
                  "mceTableInsertColAfter"
              ) {
                setTimeout(() => {
                  const table =
                    getTable(editor);

                  if (!table) {
                    return;
                  }

                  normalizeTableColumnWidths(
                    editor,
                    table
                  );

                  editor.nodeChanged();
                }, 0);
              }
            }
          );

          const openTableProperties =
            () => {
              const table =
                getTable(editor);

              if (!table) {
                return;
              }

              const imported =
                isImportedTable(table);

              const initialClass =
                getTableClassValue(
                  table
                );

              const width =
                getTableWidth(table);

              const height =
                getTableHeight(table);

              const backgroundColor =
                getTableBackgroundColor(
                  table
                );

              const borderColor =
                getTableBorderColor(
                  table
                );

              const tableStyleItems =
                imported
                  ? [
                      {
                        text:
                          "Personalizzato",
                        value:
                          "custom"
                      }
                    ]
                  : bootstrapTableStyles;

              editor.windowManager.open({
                title:
                  "Proprietà tabella",

                size: "normal",

                body: {
                  type: "tabpanel",

                  tabs: [
                    {
                      name: "general",
                      title: "Generale",

                      items: [
                        {
                          type:
                            "sizeinput",

                          name: "size",

                          label:
                            "Dimensioni"
                        },
                        {
                          type:
                            "selectbox",

                          name: "align",

                          label:
                            "Allineamento",

                          items: [
                            {
                              text:
                                "Automatico",
                              value: ""
                            },
                            {
                              text:
                                "Sinistra",
                              value:
                                "left"
                            },
                            {
                              text:
                                "Centro",
                              value:
                                "center"
                            },
                            {
                              text:
                                "Destra",
                              value:
                                "right"
                            }
                          ]
                        }
                      ]
                    },

                    {
                      name: "advanced",
                      title: "Avanzate",

                      items: [
                        {
                          type:
                            "selectbox",

                          name:
                            "tableClass",

                          label:
                            "Stile tabella",

                          items:
                            tableStyleItems,

                          enabled:
                            !imported
                        },
                        {
                          type:
                            "colorinput",

                          name:
                            "borderColor",

                          label:
                            "Colore bordo"
                        },
                        {
                          type:
                            "colorinput",

                          name:
                            "backgroundColor",

                          label:
                            "Colore sfondo"
                        }
                      ]
                    }
                  ]
                },

                initialData: {
                  size: {
                    width:
                      width || "",
                    height:
                      height || ""
                  },

                  align: imported
                    ? getImportedTableAlignment(
                        table
                      )
                    : table.getAttribute(
                        "align"
                      ) || "",

                  tableClass:
                    initialClass,

                  borderColor:
                    borderColor ||
                    (imported
                      ? ""
                      : "#dee2e6"),

                  backgroundColor:
                    backgroundColor ||
                    ""
                },

                buttons: [
                  {
                    type: "cancel",
                    text: "Annulla"
                  },
                  {
                    type: "submit",
                    text: "Salva",
                    primary: true
                  }
                ],

                onSubmit: (api) => {
                  const data =
                    api.getData();

                  editor.undoManager.transact(
                    () => {
                      if (!imported) {
                        const selectedStyle =
                          data.tableClass;

                        const styleChanged =
                          selectedStyle !==
                          initialClass;

                        bootstrapTableClasses.forEach(
                          (className) => {
                            editor.dom.removeClass(
                              table,
                              className
                            );
                          }
                        );

                        selectedStyle
                          .split(" ")
                          .filter(Boolean)
                          .forEach(
                            (className) => {
                              editor.dom.addClass(
                                table,
                                className
                              );
                            }
                          );

                        if (styleChanged) {
                          resetTableColors(
                            editor,
                            table
                          );

                          resetTableCellColors(
                            editor,
                            table
                          );
                        } else {
                          const background =
                            data.backgroundColor?.trim();

                          if (background) {
                            editor.dom.setStyle(
                              table,
                              "--bs-table-bg",
                              background
                            );

                            editor.dom.setStyle(
                              table,
                              "background-color",
                              background
                            );
                          } else {
                            editor.dom.setStyle(
                              table,
                              "--bs-table-bg",
                              ""
                            );

                            editor.dom.setStyle(
                              table,
                              "background-color",
                              ""
                            );
                          }

                          const border =
                            data.borderColor?.trim();

                          if (border) {
                            editor.dom.setStyle(
                              table,
                              "--bs-table-border-color",
                              border
                            );

                            setImportantStyle(
                              table,
                              "border-color",
                              border
                            );
                          } else {
                            editor.dom.setStyle(
                              table,
                              "--bs-table-border-color",
                              ""
                            );

                            table.style.removeProperty(
                              "border-color"
                            );
                          }
                        }

                      } else {
                        const background =
                          data.backgroundColor?.trim();

                        if (background) {
                          setImportantStyle(
                            table,
                            "background-color",
                            background
                          );
                        } else {
                          table.style.removeProperty(
                            "background-color"
                          );
                        }

                        const border =
                          data.borderColor?.trim();

                        if (border) {
                          setImportantStyle(
                            table,
                            "border-color",
                            border
                          );
                        } else {
                          table.style.removeProperty(
                            "border-color"
                          );
                        }
                      }

                      const widthValue =
                        data.size?.width?.trim();

                      const heightValue =
                        data.size?.height?.trim();

                      if (widthValue) {
                        editor.dom.setStyle(
                          table,
                          "width",
                          normalizeWidthValue(
                            widthValue
                          )
                        );
                      } else {
                        editor.dom.setStyle(
                          table,
                          "width",
                          ""
                        );
                      }

                      if (heightValue) {
                        editor.dom.setStyle(
                          table,
                          "height",
                          normalizeHeightValue(
                            heightValue
                          )
                        );
                      } else {
                        editor.dom.setStyle(
                          table,
                          "height",
                          ""
                        );
                      }

                      if (imported) {
                        editor.dom.setAttrib(
                          table,
                          "align",
                          ""
                        );

                        editor.dom.setStyle(
                          table,
                          "margin-left",
                          ""
                        );

                        editor.dom.setStyle(
                          table,
                          "margin-right",
                          ""
                        );

                        if (
                          data.align ===
                          "center"
                        ) {
                          editor.dom.setStyle(
                            table,
                            "margin-left",
                            "auto"
                          );

                          editor.dom.setStyle(
                            table,
                            "margin-right",
                            "auto"
                          );
                        } else if (
                          data.align ===
                          "right"
                        ) {
                          editor.dom.setStyle(
                            table,
                            "margin-left",
                            "auto"
                          );

                          editor.dom.setStyle(
                            table,
                            "margin-right",
                            "0"
                          );
                        }
                      } else {
                        if (data.align) {
                          editor.dom.setAttrib(
                            table,
                            "align",
                            data.align
                          );
                        } else {
                          editor.dom.setAttrib(
                            table,
                            "align",
                            ""
                          );
                        }
                      }
                    }
                  );

                  editor.nodeChanged();

                  api.close();
                }
              });
            };

          const openCellProperties =
            () => {
              const cell =
                getCell(editor);

              if (!cell) {
                return;
              }

              const table =
                getTable(editor);

              const bootstrapTable =
                isBootstrapTable(table);

              const width =
                getElementWidth(cell);

              const height =
                getElementHeight(cell);

              const backgroundColor =
                getCellBackgroundColor(
                  cell
                );

              const textColor =
                getCellTextColor(cell);

              editor.windowManager.open({
                title:
                  "Proprietà cella",

                size: "normal",

                body: {
                  type: "tabpanel",

                  tabs: [
                    {
                      name: "general",
                      title: "Generale",

                      items: [
                        {
                          type:
                            "sizeinput",

                          name: "size",

                          label:
                            "Dimensioni",

                          enabled:
                            !bootstrapTable
                        },
                        {
                          type:
                            "selectbox",

                          name:
                            "verticalAlign",

                          label:
                            "Allineamento verticale",

                          items: [
                            {
                              text:
                                "Automatico",
                              value:
                                ""
                            },
                            {
                              text:
                                "In alto",
                              value:
                                "top"
                            },
                            {
                              text:
                                "Al centro",
                              value:
                                "middle"
                            },
                            {
                              text:
                                "In basso",
                              value:
                                "bottom"
                            }
                          ],

                          enabled:
                            !bootstrapTable
                        }
                      ]
                    },

                    {
                      name: "advanced",
                      title: "Avanzate",

                      items: [
                        {
                          type:
                            "colorinput",

                          name:
                            "backgroundColor",

                          label:
                            "Colore sfondo"
                        },
                        {
                          type:
                            "colorinput",

                          name:
                            "textColor",

                          label:
                            "Colore testo"
                        }
                      ]
                    }
                  ]
                },

                initialData: {
                  size: {
                    width:
                      width || "",
                    height:
                      height || ""
                  },

                  verticalAlign:
                    cell.style
                      .verticalAlign ||
                    "",

                  backgroundColor:
                    backgroundColor ||
                    "",

                  textColor:
                    textColor || ""
                },

                buttons: [
                  {
                    type: "cancel",
                    text: "Annulla"
                  },
                  {
                    type: "submit",
                    text: "Salva",
                    primary: true
                  }
                ],

                onSubmit: (api) => {
                  const data =
                    api.getData();

                  editor.undoManager.transact(
                    () => {
                      if (!bootstrapTable) {
                        const widthValue =
                          data.size?.width?.trim();

                        const heightValue =
                          data.size?.height?.trim();

                        if (widthValue) {
                          editor.dom.setStyle(
                            cell,
                            "width",
                            normalizeWidthValue(
                              widthValue
                            )
                          );
                        } else {
                          editor.dom.setStyle(
                            cell,
                            "width",
                            ""
                          );
                        }

                        if (heightValue) {
                          editor.dom.setStyle(
                            cell,
                            "height",
                            normalizeHeightValue(
                              heightValue
                            )
                          );
                        } else {
                          editor.dom.setStyle(
                            cell,
                            "height",
                            ""
                          );
                        }

                        if (
                          data.verticalAlign
                        ) {
                          editor.dom.setStyle(
                            cell,
                            "vertical-align",
                            data.verticalAlign
                          );
                        } else {
                          editor.dom.setStyle(
                            cell,
                            "vertical-align",
                            ""
                          );
                        }
                      }

                      const background =
                        data.backgroundColor?.trim();

                      if (background) {
                        setImportantStyle(
                          cell,
                          "background-color",
                          background
                        );

                        setImportantStyle(
                          cell,
                          "--bs-table-bg",
                          background
                        );
                      } else {
                        cell.style.removeProperty(
                          "background-color"
                        );

                        cell.style.removeProperty(
                          "--bs-table-bg"
                        );
                      }

                      const color =
                        data.textColor?.trim();

                      if (color) {
                        setImportantStyle(
                          cell,
                          "color",
                          color
                        );

                        setImportantStyle(
                          cell,
                          "--bs-table-color",
                          color
                        );
                      } else {
                        cell.style.removeProperty(
                          "color"
                        );

                        cell.style.removeProperty(
                          "--bs-table-color"
                        );
                      }
                    }
                  );

                  editor.nodeChanged();

                  api.close();
                }
              });
            };

          editor.ui.registry.addButton(
            "customTableProps",
            {
              text:
                "Proprietà tabella",

              icon: "table",

              tooltip:
                "Proprietà tabella",

              onAction:
                openTableProperties
            }
          );

          editor.ui.registry.addButton(
            "customTableCellProps",
            {
              text:
                "Proprietà cella",

              icon:
                "table-cell-properties",

              tooltip:
                "Proprietà cella",

              onAction:
                openCellProperties
            }
          );

          editor.on(
            "focus",
            () => {
              editor
                .getContainer()
                .querySelector(
                  ".tox-sidebar-wrap"
                )
                ?.classList.add(
                  "editor-focused"
                );
            }
          );

          editor.on(
            "blur",
            () => {
              editor
                .getContainer()
                .querySelector(
                  ".tox-sidebar-wrap"
                )
                ?.classList.remove(
                  "editor-focused"
                );
            }
          );

          editor.on(
            "FullscreenStateChanged",
            (e) => {
              const adminMain =
                document.querySelector(
                  ".admin-main"
                );

              if (!adminMain) {
                return;
              }

              if (e.state) {
                adminMain.style.overflow =
                  "hidden";
              } else {
                adminMain.style.overflowY =
                  "auto";

                adminMain.style.overflowX =
                  "hidden";
              }
            }
          );
        },

        placeholder:
          placeholder || ""
      }}
    />
  );
};

export default TinyMCEEditor;
